import React, { useState } from 'react';

import { Loader } from '@/components/Loader';
import { Modal } from '@/components/Modal';
import { useAxios } from '@/hooks/useAxios';
import { queryClient } from '@/queryclient';
import { Technology } from '@/types';
import { cn } from '@/utils/classname';

import { CpeEditor } from './CpeEditor';

interface OsintConstantineModalProps {
  open: boolean;
  onClose: () => void;
}

type Step = 'create-technology' | 'submit-scan';

export const OsintConstantineModal: React.FC<OsintConstantineModalProps> = ({
  open,
  onClose,
}) => {
  const axios = useAxios();
  const [step, setStep] = useState<Step>('create-technology');
  const [cpe, setCpe] = useState('');
  const [technology, setTechnology] = useState<Technology | null>(null);
  const [repoUrl, setRepoUrl] = useState('');
  const [goal, setGoal] = useState('');
  const [isCreating, setIsCreating] = useState(false);
  const [isGuessing, setIsGuessing] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const resetState = () => {
    setStep('create-technology');
    setCpe('');
    setTechnology(null);
    setRepoUrl('');
    setGoal('');
    setIsCreating(false);
    setIsGuessing(false);
    setIsSubmitting(false);
    setError('');
    setSuccess('');
  };

  const handleClose = () => {
    if (!isCreating && !isSubmitting) {
      resetState();
      onClose();
    }
  };

  const handleCreateTechnology = async () => {
    if (!cpe.trim()) {
      setError('CPE string is required');
      return;
    }

    setIsCreating(true);
    setError('');
    try {
      const { data } = await axios.post('/osint/create-technology', {
        cpe: cpe.trim(),
      });

      const createdTech = data.technology as Technology;
      setTechnology(createdTech);
      setStep('submit-scan');

      // Start guessing the repo URL
      guessRepo(createdTech);
    } catch (err) {
      const axiosErr = err as { response?: { data?: { message?: string } } };
      const message =
        axiosErr?.response?.data?.message ||
        'Failed to create technology. Check the CPE format.';
      setError(message);
    } finally {
      setIsCreating(false);
    }
  };

  const guessRepo = async (tech: Technology) => {
    setIsGuessing(true);
    setError('');
    try {
      const { data } = await axios.post('/osint/guess-repo', {
        cpe: tech.cpe,
        technology_name: tech.name || `${tech.vendor} ${tech.product}`,
      });
      setRepoUrl(data.repo_url || '');
    } catch (err) {
      console.error('Failed to guess repo:', err);
      setError('Could not guess repository URL. Please enter it manually.');
    } finally {
      setIsGuessing(false);
    }
  };

  const handleSubmitScan = async () => {
    if (!repoUrl.trim()) {
      setError('Repository URL is required');
      return;
    }

    setIsSubmitting(true);
    setError('');
    try {
      await axios.post('/osint/submit', {
        repo_url: repoUrl.trim(),
        technology_key: technology?.key || undefined,
        goal: goal.trim() || undefined,
      });

      // Invalidate OSINT queries
      queryClient.invalidateQueries({
        predicate: query => {
          const queryKey = query.queryKey as string[];
          return (
            (queryKey[0] === 'MY' && queryKey.includes('osint')) ||
            queryKey[0] === 'OSINT'
          );
        },
        refetchType: 'active',
      });

      setSuccess(
        'Constantine scan initiated. Results will appear in the OSINT tab.'
      );
      setTimeout(() => {
        handleClose();
      }, 2000);
    } catch (err) {
      const axiosErr = err as { response?: { data?: { message?: string } } };
      const message =
        axiosErr?.response?.data?.message ||
        'Failed to submit scan. Please try again.';
      setError(message);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!open) return null;

  const isStepOne = step === 'create-technology';

  return (
    <Modal
      size="3xl"
      title="Research with Constantine"
      subtitle={
        isStepOne
          ? 'Step 1: Add a technology for research'
          : `Step 2: Configure scan for ${technology?.name || technology?.product || technology?.cpe}`
      }
      open={open}
      onClose={handleClose}
      disableAutoFocus
      footer={{
        text: isStepOne
          ? isCreating
            ? 'Creating...'
            : 'Next'
          : isSubmitting
            ? 'Submitting...'
            : 'Start Scan',
        onClick: isStepOne ? handleCreateTechnology : handleSubmitScan,
        disabled: isStepOne
          ? !cpe.trim() || isCreating
          : !repoUrl.trim() || isSubmitting || isGuessing || !!success,
        left: !isStepOne ? (
          <button
            onClick={() => {
              setStep('create-technology');
              setRepoUrl('');
              setGoal('');
              setError('');
            }}
            className="text-chariot-text-secondary hover:text-chariot-text-primary text-sm"
            disabled={isSubmitting || !!success}
          >
            Back
          </button>
        ) : undefined,
      }}
    >
      <div className="space-y-4">
        {error && (
          <div
            className="bg-chariot-status-critical/10 text-chariot-status-critical rounded-md p-2 text-sm"
            role="alert"
          >
            {error}
          </div>
        )}

        {success && (
          <div
            className="bg-chariot-status-positive/10 text-chariot-status-positive rounded-md p-2 text-sm"
            role="status"
          >
            {success}
          </div>
        )}

        {isStepOne ? (
          <CpeEditor
            value={cpe}
            onChange={val => {
              setCpe(val);
              if (error) setError('');
            }}
            disabled={isCreating}
          />
        ) : (
          <>
            <div>
              <label
                htmlFor="osint-repo-url"
                className="text-chariot-text-primary mb-1 block text-sm font-medium"
              >
                Repository URL
              </label>
              <div className="relative">
                <input
                  id="osint-repo-url"
                  type="url"
                  value={repoUrl}
                  onChange={e => {
                    setRepoUrl(e.target.value);
                    if (error) setError('');
                  }}
                  placeholder={
                    isGuessing
                      ? 'Guessing repository...'
                      : 'https://github.com/org/repo'
                  }
                  disabled={isGuessing || isSubmitting || !!success}
                  className={cn(
                    'border-chariot-stroke bg-chariot-background-primary text-chariot-text-primary w-full rounded-md border px-3 py-2 pr-9 text-sm',
                    'focus:border-chariot-layer-primary focus:ring-chariot-layer-primary focus:ring-1 focus:outline-hidden',
                    'disabled:cursor-not-allowed disabled:opacity-50'
                  )}
                />
                {isGuessing && (
                  <div className="absolute top-1/2 right-3 -translate-y-1/2">
                    <Loader isLoading type="spinner" />
                  </div>
                )}
              </div>
            </div>

            <div>
              <label
                htmlFor="osint-goal"
                className="text-chariot-text-primary mb-1 block text-sm font-medium"
              >
                Goal (Optional)
              </label>
              <input
                id="osint-goal"
                type="text"
                value={goal}
                onChange={e => setGoal(e.target.value)}
                placeholder="e.g., Focus on SQL injection vulnerabilities"
                disabled={isSubmitting || !!success}
                className={cn(
                  'border-chariot-stroke bg-chariot-background-primary text-chariot-text-primary w-full rounded-md border px-3 py-2 text-sm',
                  'focus:border-chariot-layer-primary focus:ring-chariot-layer-primary focus:ring-1 focus:outline-hidden',
                  'disabled:cursor-not-allowed disabled:opacity-50'
                )}
              />
              <p className="text-chariot-text-secondary mt-1 text-xs">
                Injected into all LLM system prompts to focus the analysis
              </p>
            </div>
          </>
        )}
      </div>
    </Modal>
  );
};
